import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  Chip,
  Stack,
  useTheme,
  alpha,
  Fade,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser as VerifiedIcon,
  CalendarToday as CalendarIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db} from '../../lib/firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [userData, setUserData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: '',
    phone: '',
    address: '',
  });

  useEffect(() => {
    fetchUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchUserData = async () => {
    if (currentUser) {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setFormData({
          displayName: data.displayName || currentUser.displayName || '',
          phone: data.phone || '',
          address: data.address || '',
        });
      }
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: formData.displayName,
        phone: formData.phone,
        address: formData.address,
        updatedAt: new Date().toISOString(),
      });
      
      setMessage('Profile updated successfully!');
      setEditing(false);
      fetchUserData();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setError(error.message);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    setError('');
    setMessage('');
    
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordForm.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, passwordForm.newPassword);
      
      setMessage('Password updated successfully!');
      setPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else {
        setError(error.message);
      }
      setTimeout(() => setError(''), 3000);
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleDetails = () => {
    if (isSuperAdmin) {
      return {
        label: 'Super Administrator',
        icon: <StarIcon sx={{ fontSize: 16 }} />,
        color: theme.palette.secondary.main,
        bgColor: alpha(theme.palette.secondary.main, 0.1),
        description: 'Full system access with all permissions',
      };
    }
    if (isAdmin) {
      return {
        label: 'Administrator',
        icon: <AdminIcon sx={{ fontSize: 16 }} />,
        color: theme.palette.primary.main,
        bgColor: alpha(theme.palette.primary.main, 0.1),
        description: 'Admin access with limited permissions',
      };
    }
    return {
      label: 'User',
      icon: <SecurityIcon sx={{ fontSize: 16 }} />,
      color: theme.palette.text.secondary,
      bgColor: alpha(theme.palette.text.secondary, 0.1),
      description: 'Standard user access',
    };
  };

  const roleDetails = getRoleDetails();

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 } }}>
      

      {/* Success/Error Messages */}
      <Fade in={!!message || !!error}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          {message && <Alert severity="success" onClose={() => setMessage('')} sx={{ borderRadius: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>{error}</Alert>}
        </Stack>
      </Fade>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        {/* Left Column - Profile Info */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Cover Area */}
            <Box sx={{ 
              height: 100, 
              bgcolor: alpha(roleDetails.color, 0.15),
              position: 'relative',
            }} />
            
            {/* Avatar Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -5, mb: 2 }}>
             
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: roleDetails.color,
                    border: `4px solid ${theme.palette.background.paper}`,
                    fontSize: 40,
                    fontWeight: 600,
                  }}
                >
                  {userData?.displayName?.[0] || currentUser?.email?.[0] || 'U'}
                </Avatar>
              
            </Box>

            {/* User Info */}
            <Box sx={{ textAlign: 'center', px: 3, pb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {userData?.displayName || currentUser?.displayName || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {currentUser?.email}
              </Typography>
              <Chip
                label={roleDetails.label}
                size="small"
                icon={roleDetails.icon}
                sx={{
                  bgcolor: roleDetails.bgColor,
                  color: roleDetails.color,
                  fontWeight: 600,
                  mb: 2,
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {roleDetails.description}
              </Typography>
            </Box>

            <Divider />

            {/* Account Info */}
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Account Information
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <EmailIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email Address</Typography>
                    <Typography variant="body2">{currentUser?.email}</Typography>
                  </Box>
                </Stack>
                
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Member Since</Typography>
                    <Typography variant="body2">
                      {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
                
                {userData?.approvedAt && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <VerifiedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Approved On</Typography>
                      <Typography variant="body2">
                        {new Date(userData.approvedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Actions */}
            <Box sx={{ p: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LockResetIcon />}
                onClick={() => setPasswordDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Change Password
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Right Column - Edit Profile */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {editing ? 'Edit Profile' : 'Profile Information'}
              </Typography>
              {!editing ? (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Edit Profile
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        displayName: userData?.displayName || currentUser?.displayName || '',
                        phone: userData?.phone || '',
                        address: userData?.address || '',
                      });
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleUpdate}
                    disabled={loading}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Save Changes
                  </Button>
                </Stack>
              )}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                disabled={!editing}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Email Address"
                value={currentUser?.email || ''}
                disabled
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Email address cannot be changed"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!editing}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                disabled={!editing}
                multiline
                rows={2}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <LocationIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                  readOnly: !editing,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

             
            </Stack>

           
          </Paper>
        </Box>
      </Stack>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              size="small"
              helperText="Password must be at least 6 characters"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setPasswordDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={passwordLoading}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {passwordLoading ? <CircularProgress size={24} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default Profile;